/**
 * EN-961: Sentry used to receive every Pinia store in full.
 *
 * A single event carried ~16.8 KB of inline brand SVG from `checklist`, the user's whole resume
 * document tree and undo stack from `pdf-builder`, interview transcriptions, Gmail subjects and
 * bodies, and shared-resume email addresses. That is a side door around the `send_default_pii`
 * decision recorded in `config/sentry.php`.
 *
 * This file is the complete, explicit list of what may still leave the browser. Anything not
 * named here is dropped.
 *
 *   fields - emitted as a scalar ONLY. If the value turns out to be an object, array or function
 *            it is replaced with a shape marker (`[Array(12)]`, `[Object]`, `[Function]`) and
 *            never traversed. Strings are truncated. Dot paths read one specific nested scalar.
 *   counts - emitted as `<path>Count`: array length, Map/Set size, or own-key count. Never the
 *            contents. "Did the array fill?" is the signal; the contents are the problem.
 *
 * Adding a store? Add an entry. A store with no entry is dropped AND reported under
 * `_unallowlistedStores`, so it surfaces in Sentry as unreviewed. Deliberate omissions use
 * EXCLUDED so they stay silent. `pinia-state-allowlist.test.js` fails CI until every store
 * declared in `resources/js/store/` appears below.
 */

/** Reviewed and deliberately sends nothing. */
export const EXCLUDED = Object.freeze({ fields: [] });

export const PINIA_STATE_ALLOWLIST = Object.freeze({
	// `notifications` holds fetched notification objects whose copy interpolates user data.
	activityNotifications: {
		fields: ['isNudgeVisible', 'hasUnreadNotifications'],
		counts: ['notifications'],
	},

	// `groups`, `subgroups` and `users` describe other people; `searchStr` is typed input.
	'admin-view': {
		fields: ['loading', 'groupsLoaded', 'subgroupsLoaded', 'page', 'totalPages', 'resultLength'],
		counts: ['groups', 'subgroups', 'users'],
	},

	// `queue` holds job ids and redirect timestamps for pending "Did you apply?" prompts. How
	// deep the queue is answers "did the prompt have anything to show?"; which jobs does not.
	'apply-confirmation': {
		counts: ['queue'],
	},

	// `activeFeature` is a feature key from our own source, not user input.
	'auth-popup': {
		fields: ['showAuthPopup', 'showPopupOpenAnimation', 'activeFeature'],
	},

	// auto-apply.store.js:47 `user` and :53 `generatedAnswers` (AI-written application answers)
	// are deliberately absent.
	'auto-apply': {
		fields: [
			'jobFunctionsLoaded',
			'isAutoApplyPreviewVisible',
			'isAutoApplySettingsVisible',
			'isSelectingResume',
			'currentAutoApplyJobId',
			'isAutoApplyInProgress',
			'isGenerating',
			'isPreviewHiddenForDocumentEditing',
		],
		counts: ['jobFunctions', 'generatedAnswers', 'validationErrors'],
	},

	'auto-apply-nudge': {
		fields: ['showModal', 'modalMode'],
	},

	// `counts` and `lastAdded` are objects; the glow flag and the two job ids say what the queue was doing.
	// `applications` holds job titles and companies, so only its length leaves the browser.
	'autoapply-queue': {
		fields: [
			'isTabGlowing',
			'addingJobId',
			'leavingJobId',
			'isQueueLoaded',
			'completeAutoApply',
			'isSavingCompleteAutoApply',
		],
		counts: ['applications'],
	},

	// Whether the EN-1250 tour was running, and on which step. The steps hold only copy and selectors.
	'autoapply-queue-tutorial': {
		fields: ['active', 'stepIndex'],
	},

	// Whether the EN-1251 tutorial was running, on which step, and whether it was waiting after Couldn't apply.
	'autoapply-queue-new-user-tutorial': {
		fields: ['active', 'stepIndex', 'isWaiting'],
	},

	// `uploadedResume` is an object and adds nothing over `step` reaching 'analysis'.
	'auto-apply-promo': {
		fields: ['step', 'isFinalPrompt', 'isLoadingGate', 'daysSinceUpdate'],
	},

	'builder-tools': {
		fields: ['nrOfPages', 'currentPage'],
	},

	// bullet-store.js:13-14 holds live nested store INSTANCES (scorerStore, builderStore), and
	// `data`/`title` hold the bullet text being generated.
	'bullet-generator': {
		fields: ['opened', 'index'],
	},

	'career-coach': {
		fields: ['userTier'],
	},

	// onboarding-checklist.store.js:12 - `checklistLevels` carries 5 inline brand SVG wordmarks
	// (2.9-4.2 KB each, ~16.8 KB total) with zero diagnostic value. Length only.
	checklist: {
		fields: ['currentLevelIndex', 'originalCurrentIndex', 'showOnboardingChecklist'],
		counts: ['checklistLevels', 'userChecklistLevels'],
	},

	// `progress` describes a coaching client.
	'client-portal': {
		fields: ['loading', 'loaded', 'error'],
	},

	// `commentInputValue` and `searchQuery` are typed input; the resume arrays are documents.
	community: {
		fields: [
			'loadingAddComment',
			'loadingPublish',
			'loadingFetchingResumes',
			'loadingFetchComments',
			'loadingSearch',
			'loadingReportComment',
			'showSearchResult',
			'showSearchResults',
			'showDocumentPreview',
			'showMobileDisclaimer',
			'showReportCommentModal',
			'activeSnapshotId',
			'selectedCommentId',
			'activePage',
			'lazyLoading',
		],
		counts: ['sharedResumes', 'savedResumes', 'userResumes', 'commentsForCurrentSnapshot', 'searchResult'],
	},

	// Request lists carry user-authored request text; `searchQuery` is typed input.
	'community-requests-store': {
		fields: ['showRequestModal', 'fetchingRequests', 'selectedCategory', 'selectedSort'],
		counts: ['currentRequestsList', 'fullRequestsList'],
	},

	// `coverLetters` holds titles and body content.
	'cover-letters': {
		fields: ['loading', 'loaded'],
		counts: ['coverLetters'],
	},

	'dashboard-stats': {
		fields: ['loading', 'loaded', 'failed'],
	},

	// One boolean; the events themselves are never held in the store.
	'home-baseline': {
		fields: ['homeViewedSent'],
	},

	// `accounts` holds connected Gmail addresses.
	'email-connections': {
		counts: ['accounts'],
	},

	// `recommendations` holds resume-derived job titles and salary, and `resumeUuid` identifies
	// the user's resume. Which step the flow is on is the signal.
	'existing-user-auto-apply': {
		fields: ['step', 'hadResumeOnFile', 'uploadedNewResume'],
	},

	// Which limit the user hit is the point of most paywall reports. `singleFeatureUsage` is
	// the UserFeatureUsage resource: { name, description, usage, free_limit }.
	'feature-usage': {
		fields: [
			'activeRoute',
			'showLimitExceed',
			'currentFeature',
			'singleFeatureUsage.name',
			'singleFeatureUsage.usage',
			'singleFeatureUsage.free_limit',
		],
		counts: ['userFeatureUsage'],
	},

	// interview.store.js - `questions[].transcription` is the user's spoken answer, and
	// :102-103 `cameraStream`/`microphoneStream` are live MediaStreams.
	interview: {
		fields: [
			'activeInterviewId',
			'activeQuestionIndex',
			'interviewMode',
			'loading',
			'initialized',
			'answeringInterviewLoading',
			'recordingActive',
			'canSubmitAnswer',
			'canRetakeAnswer',
			'cameraAccessGranted',
			'microphoneAccessGranted',
			'mediaDeviceInUse',
			'mediaInterrupted',
			'questionAudioBlocked',
			'interviewLength',
			'showCountdownOverlay',
			'startCountdown',
			'playingAudio',
			'userStartSpeaking',
		],
		counts: ['questions'],
	},

	'interview-onboarding': {
		fields: ['dashboardFlowOnboardingInProgress', 'interviewFlowOnboardingInProgress', 'showOnboardingModal'],
	},

	'interview-overview': {
		fields: ['showOverviewModal'],
	},

	// The interview arrays carry question text and the user's scored answers.
	interviews: {
		fields: [
			'isActivitySidebarVisible',
			'loading',
			'loaded',
			'showResultsModal',
			'selectedRound',
			'createInterviewLoading',
		],
		counts: ['interviews', 'completedInterviews', 'uncompletedInterviews', 'archivedInterviews'],
	},

	'job-functions': {
		fields: ['isJobFunctionsLoaded', 'loading'],
		counts: ['jobFunctions'],
	},

	// `otherText` is a free-text survey answer.
	'job-quality-survey': {
		fields: ['visible', 'shownForThisCycle', 'maxScrollIndex', 'surveySessionId', 'triggerReason'],
		counts: ['viewedJobIds'],
	},

	'job-search-plan': {
		fields: ['isCycleRestarted'],
	},

	// `roleInput`, `selectedRole` and `confirmedRole` are the job titles the user typed.
	'job-title-recommender': {
		fields: [
			'currentStep',
			'isRoleConfirmed',
			'isTitleEvaluationLoading',
			'isJobSavingLoading',
			'isRecommendedTitlesLoading',
			'isParsingCompleted',
			'isPrimaryResumePresent',
			'selectedType',
			'selectedOption',
		],
		counts: ['recommendedTitles', 'errors'],
	},

	// `searchKanbanBoard` is typed input; the application objects carry job and company detail.
	'job-tracker': {
		fields: ['loading', 'loaded', 'sortByDate', 'selectedTabIndex', 'isStatsFetched'],
		counts: [
			'jobTrackerApplications',
			'unreadJobTrackerUpdates',
			'activeFilters',
			'locationSelectedOptions',
			'companyNameSelectedOptions',
			'jobTitleSelectedOptions',
		],
	},

	// `jobTrackerApplications` here carries real email subject and body.
	'job-tracker-emails': {
		fields: [
			'loading',
			'loaded',
			'page',
			'totalPages',
			'resultLength',
			'showJobsBy',
			'statusFilter',
			'sortByDate',
			'sortByDateEmailsAt',
		],
		counts: ['jobTrackerApplications'],
	},

	jobAlerts: {
		fields: ['isEditModalVisible', 'jobAlertExist', 'loadingJobAlerts', 'switchPosition'],
		counts: ['jobAlerts'],
	},

	// job-board.store.js:116-117 `latitude`/`longitude` are precise geolocation; `jobTitle` and
	// `location` are typed input; `autocomplete`, `geocoder` and `geoDataPromise` are live
	// Google Maps instances and a pending promise. `queryId` is the OpenSearch query id, which
	// is the single most useful field here for reproducing a bad result set.
	jobBoard: {
		fields: [
			'loading',
			'isSavedJobsLoading',
			'lazyLoading',
			'mountPageLoading',
			'isSavedJobsLoaded',
			'generatingDocuments',
			'totalPages',
			'foundJobs',
			'isJobSearchFound',
			'isRecommendedJobFound',
			'isRecommendedJobsLoaded',
			'isRecommendedJobsInProgress',
			'showMobileJobDetails',
			'isSavedSortNewestFirst',
			'isSortByDateDescendent',
			'isSortByRelevance',
			'isSortByDateClicked',
			'isFirstSearch',
			'isSharedLinkFound',
			'isJobBoardAiOnboardingPassed',
			'isJobBoardFirstRunSeen',
			'isUsLocationBannerDismissed',
			'infiniteScrollingPage',
			'isGeoDataLoading',
			'isLocationSelected',
			'approvedRelaxationLevel',
			'currentTab',
			'queryId',
		],
		counts: [
			'savedJobs',
			'filteredJobs',
			'recommendedJobs',
			'removedJobs',
			'selectedIndustries',
			'selectedWorkSettings',
		],
	},

	'learning-hub': {
		fields: ['loading', 'isInitialized', 'userCareerStatus', 'currentVideoIndex', 'currentCourseIndex', 'nextVideoIndex'],
		counts: ['availableCourses', 'userProgress'],
	},

	// Cursor coordinates. No diagnostic value.
	'mouse-position': EXCLUDED,

	// Toast copy interpolates user data and API error text.
	notifications: {
		counts: ['notifications'],
	},

	// `openedCoverLetter` is a document.
	onboarding: {
		fields: ['isOnboarding', 'onboardingStep'],
	},

	'onboarding-flow': {
		fields: ['state', 'analyzingPhase'],
	},

	'onboarding-retention': {
		fields: [
			'firstRunActive',
			'firstApplicationSubmitted',
			'paywallSeen',
			'exitIntentDismissedThisSession',
			'showExitIntentModal',
			'showPaywallModal',
			'showCoachingModal',
			'pendingNavigation',
		],
	},

	// `members` is a roster of other users. The analytics objects are aggregates, but they are
	// objects, so they would only ever render as `[Object]` anyway - use the loading flags.
	'organization-dashboard': {
		fields: [
			'atRiskThreshold',
			'outcomesLoading',
			'funnelLoading',
			'engagementLoading',
			'atRiskLoading',
			'trendsLoading',
			'productUsageLoading',
			'membersLoading',
		],
		counts: ['members'],
	},

	// The partner object is customer branding data; the slug identifies it without the rest.
	// InstitutionalPartnerResource only ever emits slug/name/logo_url/group_slug - there is no id.
	partner: {
		fields: ['partner.slug'],
	},

	// builder.store.js:60-94. `data` (the whole resume/cover-letter tree), `history` (an
	// unbounded undo stack of full deep clones of `data`), `user` and `router` (a live Vue
	// Router instance) are all deliberately absent.
	'pdf-builder': {
		fields: [
			'id',
			'activeRoute',
			'loading',
			'saved',
			'dragging',
			'isPreview',
			'isCoverLetter',
			'isDocumentEditable',
			'isDocumentShared',
			'showPersonalInformation',
			'historyIndex',
			'downloadProgress',
			'showDownloadPopover',
			'data.uuid',
		],
		counts: ['history', 'data.sections'],
	},

	// Presentation only - margins, zoom, type size. Nothing user-authored.
	'pdf-builder-styles': {
		fields: [
			'defaultWidth',
			'leftMargin',
			'rightMargin',
			'topMargin',
			'bottomMargin',
			'isPreview',
			'zoom',
			'textSize',
			'lineHeight',
		],
	},

	// subscription.store.js:32 `stripe` is a live Stripe.js instance.
	plan: {
		fields: [
			'isSubscribe',
			'hasSubscription',
			'isOnGracePeriod',
			'isOrgProvidedPremium',
			'premiumProvider',
			'isStripeLoaded',
			'selectedOption',
		],
		counts: ['subscriptionPlans', 'premiumPlans'],
	},

	// nudge-premium.store.js:8 `closeCallBack` holds a function.
	'premium-nudge': {
		fields: ['showModal'],
	},

	'primary-resume': {
		fields: [
			'newPrimaryResumeId',
			'showPrimaryResumeInfo',
			'showConfirmModal',
			'showSelectPrimaryResume',
			'loadingSetPrimary',
			'showOfferToSetPrimaryResume',
			'primaryResume.uuid',
		],
	},

	// `snapshot` is the whole Profile page: identity, address, work eligibility and EEO answers.
	// How complete it is, and whether it loaded, is the signal.
	profile: {
		fields: ['isLoading', 'loadError.status', 'snapshot.completed', 'snapshot.total'],
	},

	// NOTE: two stores share this id - publish-resume.store.js:4 (`success`) and
	// share-job.store.js:6 (`shareLinkPopup`, `pageLink`). Both write
	// pinia.state.value['publish-resume'], so this entry is the union of the two. `pageLink` is
	// a capability URL and is deliberately absent. See the follow-up on the id collision.
	'publish-resume': {
		fields: ['success', 'shareLinkPopup'],
	},

	// `breakdown` and `targetSelection` carry the scored resume content and target job title.
	'resume-scorer': {
		fields: [
			'showResumeScorer',
			'showBreakdownModal',
			'total',
			'maxTotal',
			'alignmentStatus',
			'scoreVersion',
			'isLoading',
		],
		counts: ['suggestions'],
	},

	// share-resume.store.js - `email`, `selectedUsers`, `sharedWith` and `userToBeRemoved` are
	// third-party email addresses and names.
	resumeShare: {
		fields: [
			'showShareModal',
			'showRemoveUserModal',
			'loadingCopyShareLink',
			'loadingShareResume',
			'generalAccessValue',
			'anyoneWithLinkAccessLevel',
		],
		counts: ['selectedUsers', 'sharedWith', 'invitedUsersAccessRights', 'validationErrors'],
	},

	// The resume arrays hold document records.
	resumes: {
		fields: ['loading', 'loaded', 'duplicateLoading', 'hasResumesLoaded'],
		counts: ['resumes', 'formattedResumes', 'unformattedResumes'],
	},

	// `unableToTailorJobTitle` is the user's target job title.
	selectResumeStore: {
		fields: [
			'isResumeModalVisible',
			'isUploadCustomModalVisible',
			'showUnableToTailorModal',
			'showSideTab',
			'unableToTailorError',
			'selectedResume.uuid',
		],
	},

	'services-nudges': {
		fields: ['showModal', 'selectedFeature', 'ctaClicked'],
	},

	sidebar: {
		fields: ['collapsed'],
	},

	'subscription-tracker': {
		fields: ['currentTracker'],
	},

	// tiptap.store.js:5 `editor` is a live TipTap Editor holding the whole ProseMirror document,
	// and `selectedText` is whatever the user highlighted. The focus flags are safe.
	tiptap: {
		fields: ['isFocused', 'selectionUpdated', 'isVisibleEditLnk', 'keepEditorFocus', 'isEnableRegenerateText'],
	},

	'token-store': {
		fields: ['tokens', 'selectedOption'],
		counts: ['options'],
	},

	// `eventHistory` records what the user did, keyed to them.
	userEvents: {
		counts: ['eventHistory'],
	},
});

/**
 * Pinia actions that fire on every typing pause or selection change. Left unfiltered they fill
 * Sentry's 100-entry breadcrumb ring within seconds of typing, evicting the navigation, fetch
 * and click breadcrumbs that actually explain the error.
 *
 * Entries are `storeId|actionName`. The store id is not available to `actionTransformer` (it
 * only receives the name), so this is applied in `beforeBreadcrumb` instead - see
 * `isNoisyPiniaBreadcrumb` in `pinia-state.js`.
 */
export const NOISY_PINIA_ACTIONS = new Set([
	'pdf-builder|changeSectionKey',
	'pdf-builder|changeSectionDate',
	'pdf-builder|changeSectionDateRange',
	'pdf-builder|changeSectionName',
	'pdf-builder|changeExperience',
	'pdf-builder|changeTitle',
	'pdf-builder|changeName',
	'pdf-builder|changeContact',
	'pdf-builder|changeMessage',
	'pdf-builder|updateMessage',
	'pdf-builder|updateSection',
	'pdf-builder|saveHistory',
	'tiptap|setSelectionUpdated',
	'tiptap|setFocused',
	'tiptap|setSelectedText',
	'tiptap|setKeepEditorFocused',
	'mouse-position|mouseEventHandler',
]);
